/* El export de seguimiento de la ART (P-173, Natalia).
 *
 * ── QUÉ ES EL ARCHIVO ────────────────────────────────────────────────────
 *
 * «ObservacionesYSeguimientos (N).xls» que Natalia baja del portal de la ART.
 * NO es un Excel binario: es una **tabla HTML** con extensión .xls (por eso no
 * se puede previsualizar). Trae, para UN trabajador, la «Tabla de
 * Seguimientos» con cuatro columnas:
 *
 *   Fecha de último turno · Motivo · Diagnóstico · Evolución
 *
 * El archivo NO dice de quién es —no trae nombre, DNI ni número de siniestro—,
 * así que la persona la elige quien sube el archivo en la pantalla, y el DNI se
 * agrega afuera de esto.
 *
 * ── POR QUÉ SE PARSEA CON REGEX Y NO CON DOMParser ───────────────────────
 *
 * El HTML del portal es pobre y a veces trae `</tr>` de más; un parser estricto
 * se corta. La tabla es plana (sin celdas anidadas), así que un barrido por
 * filas y celdas con regex es más robusto acá que armar un DOM. Es lo mismo que
 * hace el lado del bot para las planillas que vienen de sistemas de terceros.
 */

const MESES = {
  ene: 1, feb: 2, mar: 3, abr: 4, may: 5, jun: 6,
  jul: 7, ago: 8, sep: 9, sept: 9, oct: 10, nov: 11, dic: 12,
};

/* «7 sept 2026» / «24 ago 2026» / «6 may 2026» → «2026-09-07». `null` si no se
 * puede leer: una fecha inventada en un dato médico es peor que ninguna. */
export function parseFechaTurno(txt) {
  const s = String(txt || "").trim().toLowerCase();
  const m = s.match(/(\d{1,2})\s*(?:de\s+)?([a-záéíóú.]+)\.?\s*(?:de\s+)?(\d{4})/i);
  if (!m) return null;
  const dia = Number(m[1]);
  const mesTxt = m[2].normalize("NFD").replace(/[\u0300-\u036f.]/g, "").slice(0, 4);
  const mes = MESES[mesTxt] ?? MESES[mesTxt.slice(0, 3)];
  const anio = Number(m[3]);
  if (!mes || dia < 1 || dia > 31) return null;
  return `${anio}-${String(mes).padStart(2, "0")}-${String(dia).padStart(2, "0")}`;
}

const ENTIDADES = {
  "&amp;": "&", "&lt;": "<", "&gt;": ">", "&quot;": '"',
  "&#39;": "'", "&apos;": "'", "&nbsp;": " ",
};

function limpiarCelda(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/&#(\d+);/g, (_, n) => String.fromCharCode(Number(n)))
    .replace(/&[a-z]+;/gi, (e) => ENTIDADES[e.toLowerCase()] ?? " ")
    .replace(/\s+/g, " ")
    .trim();
}

/* Hash corto y estable de una fila, para no duplicar al re-subir el mismo
 * export. No es criptográfico: sólo tiene que ser el mismo para la misma fila.
 * djb2, en base36. */
function firmaDe(fecha, motivo, evolucion) {
  const base = `${fecha || ""}|${motivo || ""}|${evolucion || ""}`;
  let h = 5381;
  for (let i = 0; i < base.length; i++) h = ((h << 5) + h + base.charCodeAt(i)) >>> 0;
  return h.toString(36);
}

const ES_ENCABEZADO = (celdas) =>
  /fecha/i.test(celdas[0] || "") && /motivo/i.test(celdas[1] || "");

/* Del HTML del export a las filas del seguimiento. PURA.
 *
 * `[{fecha_turno, motivo, diagnostico, evolucion, firma}]`, saltando el
 * encabezado y las filas vacías. Nunca lanza: un archivo que no es esta tabla
 * devuelve `[]`, y la pantalla lo dice — no explota. */
export function parseSeguimientos(html) {
  const texto = String(html || "");
  const filas = texto.match(/<tr[\s\S]*?<\/tr>/gi) || [];
  const out = [];
  for (const tr of filas) {
    const celdas = (tr.match(/<t[dh][\s\S]*?<\/t[dh]>/gi) || []).map(limpiarCelda);
    if (celdas.length === 0) continue;
    if (ES_ENCABEZADO(celdas)) continue;
    const [fecha_raw = "", motivo = "", diagnostico = "", evolucion = ""] = celdas;
    // Una fila sin fecha Y sin evolución no es un seguimiento: es ruido de la
    // maquetación del portal.
    if (!fecha_raw.trim() && !evolucion.trim()) continue;
    const fecha_turno = parseFechaTurno(fecha_raw);
    out.push({
      fecha_turno,
      fecha_texto: fecha_raw,
      motivo,
      diagnostico,
      evolucion,
      firma: firmaDe(fecha_turno || fecha_raw, motivo, evolucion),
    });
  }
  return out;
}
